import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as L from 'leaflet';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { AttendanceAttemptCreatePayload, AttendanceAttemptRecord } from './attendance-attempt.interface';

type AttendanceAttemptWorkspaceMode = 'view' | 'create';

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

interface AttendanceAttemptFormValue {
  employeeId: string;
  source: string;
  eventType: string;
  eventAt: string;
  timezone: string;
  reasonCode: string;
  deviceId: string;
  deviceVersion: string;
  lat: number | '';
  lng: number | '';
  accuracy: number | '';
  faceValidationStatus: string;
  rawPayloadText: string;
  isActive: boolean;
}

@Component({
  selector: 'app-attendance-attempt-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    LoadingStateComponent,
    DateTimePickerFieldComponent,
    CanAccessDirective,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './attendance-attempt-detail.component.html',
  styleUrl: './attendance-attempt-detail.component.scss',
})
export class AttendanceAttemptDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('attendanceAttemptMap') private mapRef?: ElementRef<HTMLDivElement>;
  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  mode: AttendanceAttemptWorkspaceMode = 'view';
  employees: EmployeeRecord[] = [];
  selectedAttemptId: string | null = null;
  selectedAttempt: AttendanceAttemptRecord | null = null;
  form: AttendanceAttemptFormValue = this.createEmptyForm();
  private map: L.Map | null = null;
  private marker: L.Marker | null = null;

  readonly readAccess = { resource: 'attendanceAttempts', action: 'read' } as const;
  readonly createAccess = { resource: 'attendanceAttempts', action: 'create' } as const;
  readonly deleteAccess = { resource: 'attendanceAttempts', action: 'delete' } as const;

  readonly sourceOptions = ['device', 'mobile', 'web', 'manual'];
  readonly eventTypeOptions = ['entry', 'exit', 'meal_start', 'meal_end', 'break_start', 'break_end', 'unknown'];
  readonly reasonCodeOptions = [
    'outside_allowed_zone',
    'face_validation_failed',
    'device_not_registered',
    'device_not_bound',
    'offline_not_allowed',
    'duplicate_attempt',
  ];
  readonly faceValidationStatusOptions = ['passed', 'failed', 'pending', 'not_required'];

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    void this.loadBaseData().then(() => this.syncWithRoute());
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.renderMap(), 0);
  }

  ngOnDestroy(): void {
    this.destroyMap();
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('attendanceAttemptsCatalog.detail.createTitle');
    }
    return this.t('attendanceAttemptsCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('attendanceAttemptsCatalog.detail.createDescription');
    }
    return this.t('attendanceAttemptsCatalog.detail.description');
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get hasLocation(): boolean {
    return this.isCoordinate(this.selectedAttempt?.lat) && this.isCoordinate(this.selectedAttempt?.lng);
  }

  get hasFaceValidation(): boolean {
    return !!this.selectedAttempt?.faceValidationStatus;
  }

  get hasRawPayload(): boolean {
    const payload = this.selectedAttempt?.rawPayload;
    return !!payload && Object.keys(payload).length > 0;
  }

  get employeeInitials(): string {
    const name = this.selectedAttempt?.employeeLabel || this.selectedAttempt?.employeeName || '';
    const initials = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase()).join('');
    return initials || '—';
  }

  get eventDateLabel(): string {
    return this.formatEventDatePart('date');
  }

  get eventTimeLabel(): string {
    return this.formatEventDatePart('time');
  }

  get eventSourceIcon(): string {
    switch (`${this.selectedAttempt?.source || ''}`.toLowerCase()) {
      case 'mobile': return 'smartphone';
      case 'web': return 'language';
      case 'manual': return 'edit_note';
      case 'kiosk': return 'point_of_sale';
      default: return 'watch';
    }
  }

  get reasonTechnicalCode(): string {
    const value = `${this.selectedAttempt?.reasonCode || 'unknown'}`;
    return value
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_')
      .toUpperCase();
  }

  get reasonDescription(): string {
    const code = this.reasonTechnicalCode;
    if (code.includes('DUPLICATE')) return 'Este intento no generó un nuevo marcaje porque ya existía un evento equivalente.';
    if (code.includes('OUTSIDE') || code.includes('ZONE')) return 'El intento se realizó fuera de la zona permitida para el colaborador.';
    if (code.includes('FACE')) return 'La validación facial no cumplió con los requisitos configurados.';
    if (code.includes('NOT_REGISTERED')) return 'El dispositivo que envió el intento no está registrado.';
    if (code.includes('NOT_BOUND')) return 'El dispositivo no está vinculado con el colaborador.';
    if (code.includes('OFFLINE')) return 'La configuración actual no permite registrar este intento sin conexión.';
    return 'El intento fue descartado durante el procesamiento y no generó un marcaje.';
  }

  onLocationToggle(event: Event): void {
    if ((event.currentTarget as HTMLDetailsElement).open) {
      setTimeout(() => this.renderMap(), 0);
    }
  }

  goBackToList(): void {
    void this.router.navigate(['/attendance/attendance-attempts']);
  }

  cancelEdit(): void {
    if (this.selectedAttempt) {
      this.mode = 'view';
      this.form = this.createFormFromAttempt(this.selectedAttempt);
      void this.router.navigate(['/attendance/attendance-attempts', this.selectedAttempt.attendanceAttemptId]);
      return;
    }

    this.goBackToList();
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isCreateMode) {
        const payload = this.buildCreatePayload();
        const created = await firstValueFrom(this.apiService.post<AttendanceAttemptRecord>('attendance/attempts', payload));
        this.showNotification('snackbar-success', this.t('attendanceAttemptsCatalog.notifications.created'), 'bottom', 'center');
        void this.router.navigate(['/attendance/attendance-attempts', created.attendanceAttemptId]);
        return;
      }

    } catch (error) {
      console.error('Error saving attendance attempt', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceAttemptsCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedAttempt || !this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('attendanceAttemptsCatalog.dialog.deleteTitle'),
        message: this.t('attendanceAttemptsCatalog.dialog.deleteMessage', { event: this.selectedAttempt.employeeNumber }),
        confirmLabel: this.t('attendanceAttemptsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedAttemptId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance/attempts/${this.selectedAttemptId}`));
        this.showNotification('snackbar-success', this.t('attendanceAttemptsCatalog.notifications.deleted'), 'bottom', 'center');
        void this.router.navigate(['/attendance/attendance-attempts']);
      } catch (error) {
        console.error('Error deleting attendance attempt', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceAttemptsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  private async loadBaseData(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees'));
      this.employees = this.extractEmployees(response)
        .filter((employee) => employee.isActive !== false)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));
    } catch (error) {
      console.error('Error loading employees for attendance attempts', error);
      this.employees = [];
    } finally {
      this.isLoading = false;
    }
  }

  private async syncWithRoute(): Promise<void> {
    const attendanceAttemptId = this.route.snapshot.paramMap.get('attendanceAttemptId');
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/attendance-attempts/new');

    if (isCreateRoute) {
      this.enterCreateMode();
      return;
    }

    if (!attendanceAttemptId) {
      void this.router.navigate(['/attendance/attendance-attempts']);
      return;
    }

    this.isLoading = true;
    try {
      const attempt = await firstValueFrom(this.apiService.get<AttendanceAttemptRecord>(`attendance/attempts/${attendanceAttemptId}`));
      this.selectedAttempt = this.normalizeAttempt(attempt);
      this.selectedAttemptId = this.selectedAttempt.attendanceAttemptId;
      this.form = this.createFormFromAttempt(this.selectedAttempt);
      this.mode = 'view';
      setTimeout(() => this.renderMap(), 0);
    } catch (error) {
      console.error('Error loading attendance attempt detail', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('attendanceAttemptsCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
      void this.router.navigate(['/attendance/attendance-attempts']);
    } finally {
      this.isLoading = false;
    }
  }

  private enterCreateMode(): void {
    this.mode = 'create';
    this.selectedAttempt = null;
    this.selectedAttemptId = null;
    this.form = this.createEmptyForm();
    this.destroyMap();
    this.isLoading = false;
  }

  private createEmptyForm(): AttendanceAttemptFormValue {
    return {
      employeeId: '',
      source: 'device',
      eventType: 'entrada',
      eventAt: this.toDateTimeLocal(new Date().toISOString()),
      timezone: 'America/Cancun',
      reasonCode: '',
      deviceId: '',
      deviceVersion: '',
      lat: '',
      lng: '',
      accuracy: '',
      faceValidationStatus: '',
      rawPayloadText: '{\n  "deviceEventId": ""\n}',
      isActive: true,
    };
  }

  private createFormFromAttempt(attempt: AttendanceAttemptRecord): AttendanceAttemptFormValue {
    return {
      employeeId: attempt.employeeId,
      source: attempt.source,
      eventType: this.normalizeEventType(attempt.eventType),
      eventAt: this.toDateTimeLocal(attempt.eventAt),
      timezone: attempt.timezone || 'America/Cancun',
      reasonCode: attempt.reasonCode || '',
      deviceId: attempt.deviceId || '',
      deviceVersion: attempt.deviceVersion || '',
      lat: attempt.lat ?? '',
      lng: attempt.lng ?? '',
      accuracy: attempt.accuracy ?? '',
      faceValidationStatus: attempt.faceValidationStatus || '',
      rawPayloadText: JSON.stringify(attempt.rawPayload || {}, null, 2),
      isActive: attempt.isActive !== false,
    };
  }

  private buildCreatePayload(): AttendanceAttemptCreatePayload {
    return {
      employeeId: this.form.employeeId,
      source: this.form.source,
      eventType: this.form.eventType,
      eventAt: new Date(this.form.eventAt).toISOString(),
      timezone: this.form.timezone.trim(),
      reasonCode: this.form.reasonCode,
      ...(this.form.deviceId.trim() ? { deviceId: this.form.deviceId.trim() } : {}),
      ...(this.form.deviceVersion.trim() ? { deviceVersion: this.form.deviceVersion.trim() } : {}),
      ...(this.form.lat !== '' ? { lat: Number(this.form.lat) } : {}),
      ...(this.form.lng !== '' ? { lng: Number(this.form.lng) } : {}),
      ...(this.form.accuracy !== '' ? { accuracy: Number(this.form.accuracy) } : {}),
      ...(this.form.faceValidationStatus ? { faceValidationStatus: this.form.faceValidationStatus } : {}),
      ...(this.parseRawPayload() ? { rawPayload: this.parseRawPayload()! } : {}),
    };
  }

  private validateForm(): string | null {
    if (!this.form.employeeId) {
      return this.t('attendanceAttemptsCatalog.notifications.validationEmployee');
    }
    if (!this.form.source) {
      return this.t('attendanceAttemptsCatalog.notifications.validationSource');
    }
    if (!this.form.eventType) {
      return this.t('attendanceAttemptsCatalog.notifications.validationEventType');
    }
    if (!this.form.eventAt) {
      return this.t('attendanceAttemptsCatalog.notifications.validationEventAt');
    }
    if (!this.form.timezone.trim()) {
      return this.t('attendanceAttemptsCatalog.notifications.validationTimezone');
    }
    if (!this.form.reasonCode) {
      return this.t('attendanceAttemptsCatalog.notifications.validationReasonCode');
    }
    if (this.form.rawPayloadText.trim()) {
      try {
        JSON.parse(this.form.rawPayloadText);
      } catch {
        return this.t('attendanceAttemptsCatalog.notifications.validationRawPayload');
      }
    }
    return null;
  }

  private parseRawPayload(): Record<string, unknown> | null {
    if (!this.form.rawPayloadText.trim()) {
      return null;
    }
    return JSON.parse(this.form.rawPayloadText);
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private normalizeAttempt(attempt: Partial<AttendanceAttemptRecord>): AttendanceAttemptRecord {
    const source = `${attempt.source || 'device'}`.toLowerCase();
    const faceValidationStatus = attempt.faceValidationStatus || null;
    const reasonCode = attempt.reasonCode || null;
    const employee = this.employees.find((item) => item.employeeId === attempt.employeeId);
    return {
      attendanceAttemptId: attempt.attendanceAttemptId || crypto.randomUUID(),
      center: attempt.center || '',
      employeeId: attempt.employeeId || '',
      employeeNumber: attempt.employeeNumber || employee?.employeeNumber || '-',
      companiesRecordId: attempt.companiesRecordId || '',
      orgUnitId: attempt.orgUnitId || null,
      orgPath: attempt.orgPath || null,
      source,
      eventType: this.normalizeEventType(attempt.eventType),
      eventAt: attempt.eventAt || '',
      timezone: attempt.timezone || 'America/Cancun',
      deviceId: attempt.deviceId || null,
      deviceVersion: attempt.deviceVersion || null,
      lat: attempt.lat ?? null,
      lng: attempt.lng ?? null,
      accuracy: attempt.accuracy ?? null,
      faceValidationStatus,
      reasonCode,
      rawPayload: attempt.rawPayload || null,
      isActive: attempt.isActive !== false,
      employeeName: attempt.employeeName || employee?.fullName || attempt.employeeNumber || '-',
      employeeLabel: attempt.employeeName || employee?.fullName || attempt.employeeNumber || '-',
      sourceLabel: this.t(`attendanceAttemptsCatalog.sources.${source}`),
      eventTypeLabel: attempt.eventType ? this.t(`attendanceAttemptsCatalog.eventTypes.${attempt.eventType}`) : '-',
      eventAtLabel: this.formatDateTime(attempt.eventAt, attempt.timezone || 'America/Cancun'),
      faceValidationStatusLabel: faceValidationStatus ? this.t(`attendanceAttemptsCatalog.faceStatuses.${faceValidationStatus}`) : '-',
      reasonCodeLabel: this.getReasonCodeLabel(reasonCode),
      activeLabel: attempt.isActive !== false ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private renderMap(): void {
    const container = this.mapRef?.nativeElement;
    if (!container || !this.hasLocation || !this.selectedAttempt) {
      return;
    }

    const latLng: L.LatLngTuple = [Number(this.selectedAttempt.lat), Number(this.selectedAttempt.lng)];
    if (this.map && this.map.getContainer() !== container) {
      this.destroyMap();
    }

    if (!this.map) {
      this.map = L.map(container, { zoomControl: true, attributionControl: true }).setView(latLng, 16);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(this.map);
    }

    this.marker?.remove();
    this.marker = L.marker(latLng).addTo(this.map);
    this.marker.bindPopup(`
      <div class="attendance-attempt-map-popup">
        <strong>${this.selectedAttempt.employeeLabel || this.selectedAttempt.employeeNumber || '-'}</strong>
        <span>${this.selectedAttempt.eventAtLabel || '-'}</span>
        <span>${latLng[0].toFixed(6)}, ${latLng[1].toFixed(6)}</span>
        <span>${this.selectedAttempt.accuracy ?? '-'} m</span>
      </div>
    `);
    this.map.setView(latLng, Math.max(this.map.getZoom(), 16));
    setTimeout(() => this.map?.invalidateSize(), 0);
  }

  private destroyMap(): void {
    this.marker = null;
    this.map?.remove();
    this.map = null;
  }

  private isCoordinate(value: number | string | null | undefined): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    const coordinate = Number(value);
    return Number.isFinite(coordinate);
  }

  private normalizeEventType(value?: string | number | null): string {
    if (value === 0 || value === '0') {
      return 'entry';
    }
    if (value === 1 || value === '1') {
      return 'exit';
    }
    if (value === 'entrada') {
      return 'entry';
    }
    if (value === 'salida') {
      return 'exit';
    }
    return String(value ?? '');
  }

  private getReasonCodeLabel(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const key = `attendanceAttemptsCatalog.reasonCodes.${value}`;
    const translated = this.t(key);
    if (translated && translated !== key) {
      return translated;
    }

    return value
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  private toDateTimeLocal(value?: string | null): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const pad = (part: number) => `${part}`.padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private formatDateTime(value?: string | null, timezone?: string): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private formatEventDatePart(part: 'date' | 'time'): string {
    const value = this.selectedAttempt?.eventAt;
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    const timeZone = this.selectedAttempt?.timezone || 'America/Cancun';
    return part === 'date'
      ? new Intl.DateTimeFormat(locale, { timeZone, day: '2-digit', month: '2-digit', year: 'numeric' }).format(date)
      : new Intl.DateTimeFormat(locale, { timeZone, hour: '2-digit', minute: '2-digit' }).format(date);
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'attendanceAttempts', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}



